import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './MobileSheet.css';

interface MobileSheetProps {
  /** Accessible name for the dialog, e.g. "Filters". */
  ariaLabel: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Footer commit button. Omit for a sheet whose rows are themselves the actions. */
  applyLabel?: string;
  onApply?: () => void;
  /** Custom pinned footer (the inbox details sheet's action buttons). Wins over applyLabel. */
  footer?: React.ReactNode;
  /** `tall` pins the sheet 96px from the top with a 16px radius (inbox details sheet). */
  variant?: 'default' | 'tall';
}

/**
 * The bottom-sheet shell: portal, body scroll lock, Escape, overlay, drag
 * handle, scrolling body, optional footer.
 *
 * Extracted from MobileFilterSheet, which had hardcoded the search FilterPanel
 * as its body and was typed to SearchFilterState. CardActionMenu had already
 * copied the same shell ("same shell as MobileFilterSheet"), so this was on its
 * way to a third copy. The CSS moved with it verbatim -- it is fully
 * self-contained and token-free, which is what made it copyable in the first
 * place.
 */
export const MobileSheet: React.FC<MobileSheetProps> = ({
  ariaLabel,
  onClose,
  children,
  applyLabel,
  onApply,
  footer,
  variant = 'default',
}) => {
  // The page behind the sheet must not scroll with it.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Portalled to the body: .main-content-wrapper has overflow:hidden and would
  // otherwise clip the sheet.
  return createPortal(
    <>
      <div className="mobile-sheet-overlay" onClick={onClose} aria-hidden="true" />
      <div className={`mobile-filter-sheet${variant === 'tall' ? ' mobile-filter-sheet--tall' : ''}`} role="dialog" aria-modal="true" aria-label={ariaLabel}>
        <button
          type="button"
          className="mobile-sheet-handle"
          onClick={onClose}
          aria-label={`Close ${ariaLabel.toLowerCase()}`}
        >
          <span className="mobile-sheet-handle-bar" />
        </button>

        <div className="mobile-sheet-body">{children}</div>

        {footer ? (
          <div className="mobile-sheet-footer">{footer}</div>
        ) : applyLabel && (
          <div className="mobile-sheet-footer">
            <button type="button" className="mobile-sheet-apply" onClick={onApply || onClose}>
              {applyLabel}
            </button>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};

export default MobileSheet;
